import { Component, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
// import { AppInsightsService } from '@bp-core/src/lib/modules/app-insights/app-insights.service';
import { AppInteractionService } from '@bp-core/src/lib/services/app-interaction.service';
import { environment } from '@environment/environment';
import * as Sentry from '@sentry/angular';
import { Angulartics2Facebook } from 'angulartics2';
// import { Angulartics2GoogleGlobalSiteTag, Angulartics2GoogleTagManager } from 'angulartics2';

import { BpNativeStatusBarService } from 'bp/shared/services/bp-native-status-bar.service';
import { ComponentFactoryService } from 'bp/shared/services/component-factory.service';
import { PortalConfigParamService } from 'bp/shared/services/portal-config-param.service';
import { AsesorMontajeComponent } from 'bp/modules/asesor-ia/components/asesor-montaje.component';
import { NgxSonnerToaster } from 'ngx-sonner';
import {
  OAuthEvent,
  OAuthService,
  OAuthSuccessEvent,
} from 'projects/bp-core/src/lib/external/angular-oauth2-oidc-jwks/public_api';
import { AuthService } from 'projects/bp-core/src/lib/services/portal/auth.service';
import { MaintenanceRealtimeService } from 'projects/bp-core/src/lib/services/maintenance-realtime.service';
import { VersionCheckService } from 'projects/bp-core/src/lib/services/version-check.service';
import { WVCommunicationService } from 'projects/bp-core/src/lib/services/wvcommunication.service';
import { filter } from 'rxjs';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, NgxSonnerToaster, AsesorMontajeComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private router: Router,
    private oauthService: OAuthService,
    private authService: AuthService,
    private appInteractionService: AppInteractionService,
    // private angulartics: Angulartics2GoogleGlobalSiteTag,
    // private angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private wvCommunicationService: WVCommunicationService,
    private versionCheckService: VersionCheckService,
    private maintenanceService: MaintenanceRealtimeService,
    private viewContainerRef: ViewContainerRef,
    private componentFactoryService: ComponentFactoryService,
    private portalConfigParamService: PortalConfigParamService,
    private bpNativeStatusBarService: BpNativeStatusBarService,
    private angulartics2Facebook: Angulartics2Facebook,
    private snackBar: MatSnackBar,
    // private appInsights: AppInsightsService,

    private readonly swUpdate: SwUpdate,
  ) {
    this.angulartics2Facebook.startTracking();

    // this.appInsights.addOrUpdateTags({
    //   environment: environment.name,
    //   version: environment.version,
    // });
    window.onerror = (message, source, lineno, colno, error) => {
      Sentry.captureException(error);
      console.error('error', message, source, lineno, colno, error);
    };

    // Silencia el rejection benigno "AbortError: Transition was skipped" que emite
    // internamente el Router (View Transitions API) cuando una navegación interrumpe
    // la transición de otra (p.ej. el patrón bounce por '/' para forzar re-fetch).
    // La navegación se completa igual; solo es ruido en consola. Filtramos SOLO ese
    // caso exacto para no ocultar errores reales.
    // OJO: el AbortError llega como DOMException, NO como Error — `instanceof Error`
    // da false en Chrome, por eso se chequea con duck-typing sobre name/message.
    window.addEventListener('unhandledrejection', event => {
      const reason = event.reason as { name?: string; message?: string } | undefined;
      if (
        reason?.name === 'AbortError' &&
        typeof reason.message === 'string' &&
        reason.message.includes('Transition was skipped')
      ) {
        event.preventDefault();
      }
    });
    this.componentFactoryService.setViewContainerRef(this.viewContainerRef);

    swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(evt => {
        console.info('versionUpdates', evt);
        console.log('actualizando nueva version');

        const snackBarRef = this.snackBar.open(
          'Nueva versión disponible, actualizando...',
          'Cancelar',
          {
            duration: 3000,
            panelClass: ['snack-success'],
          },
        );

        snackBarRef.afterDismissed().subscribe(dis => {
          if (!dis.dismissedByAction) {
            console.log('dismissed');
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        });

        snackBarRef.onAction().subscribe(dis => {
          console.log('Cancelada la recarga');
        });
      });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
      console.log('se navego a la URL:', (event as NavigationEnd).url);
      this.wvCommunicationService.setNavigationEnd(
        window.location.origin,
        (event as NavigationEnd).url,
      );
    });
    this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(event => {
      console.log('Intentando navegar a la URL:', (event as NavigationStart).url);
      this.wvCommunicationService.setNavigationStart(
        window.location.origin,
        (event as NavigationStart).url,
      );
    });

    // this.angulartics.startTracking();
    // this.angulartics2GoogleTagManager.startTracking();
    
    // Inicializar servicio de mantenimiento Supabase
    this.maintenanceService.initialize(environment.supabase);
    
    // Escuchar cambios de estado de mantenimiento
    this.maintenanceService.maintenanceStatus$.subscribe(status => {
      if (status?.in_maintenance) {
        // Solo redirigir si NO estamos ya en página de mantenimiento
        if (!this.maintenanceService.isCurrentlyOnMaintenancePage()) {
          const maintenanceUrl = this.maintenanceService.getMaintenanceUrlWithCallback();
          console.log('🔄 Redirigiendo a mantenimiento con callback:', maintenanceUrl);
          console.log('🔄 URL actual capturada:', window.location.href);
          this.router.navigateByUrl(maintenanceUrl);
        }
      }
    });

    // Verificar estado inicial de mantenimiento después de la inicialización
    setTimeout(() => {
      const currentStatus = this.maintenanceService.getCurrentStatus();
      if (currentStatus?.in_maintenance && !this.maintenanceService.isCurrentlyOnMaintenancePage()) {
        const maintenanceUrl = this.maintenanceService.getMaintenanceUrlWithCallback();
        console.log('🔄 Sistema ya en mantenimiento, redirigiendo con callback:', maintenanceUrl);
        console.log('🔄 URL actual capturada:', window.location.href);
        this.router.navigateByUrl(maintenanceUrl);
      }
    }, 1000); // Dar tiempo para que se cargue el estado inicial

    this.oauthService.configure({
      issuer: environment.auth.issuer,
      // Exigir HTTPS solo cuando el issuer es HTTPS (prod/dev cloud). En local
      // el issuer puede ser http://<ip> (LAN/Tailscale) además de localhost, y
      // la excepción integrada de la librería solo cubre localhost.
      requireHttps: environment.auth.issuer.startsWith('https'),
      clientId: environment.auth.client_id_code,
      scope: 'openid profile email apiv1 apiv2 offline_access IdentityServerApi',
      redirectUri: window.location.origin + '/auth/login/',
      silentRefreshRedirectUri: `${window.location.origin}/silent-refresh.html`,
      useSilentRefresh: true,
      sessionChecksEnabled: true,
      clockSkewInSec: 86400,
      // timeoutFactor: 0.01,
      clearHashAfterLogin: false,
      // sessionChecksEnabled: true,
      // tokenEndpoint: environment.auth.issuer + '/connect/token',
      // userinfoEndpoint: environment.auth.issuer + '/connect/userinfo'
      // Authorization Code Flow (+PKCE). Nota de seguridad: NO se configura un
      // validation handler de firma (queda el NullValidationHandler default) y
      // eso es CORRECTO para code flow — el id_token llega por el POST directo
      // al token endpoint sobre TLS y la spec OIDC (3.1.3.7) permite apoyarse
      // en esa validación de canal; la propia librería retiró el
      // JwksValidationHandler del core por esta razón (el que queda en
      // token-validation/ es un dummy). Si algún día se cambia a un flujo que
      // reciba tokens por canal indirecto, ahí SÍ se necesita un validador real.
      responseType: 'code',
      // El debug del flujo OAuth queda solo fuera de producción: en prod
      // logueaba cada paso del flujo en la consola de cualquier usuario.
      showDebugInformation: !environment.production,
      postLogoutRedirectUri: window.location.origin,
      logoutUrl: `${environment.auth.issuer}/Identity/Account/Logout?returnUrl=${window.location.origin}`,
      // Cachea el discovery document y el jwks (contenido estatico por entorno)
      // en localStorage, atados al issuer exacto. Elimina los dos GET seriales
      // del arranque (~640 ms medidos en dev) sirviendo desde cache con 0
      // round-trips y revalidando contra la red en background.
      // Solo el portal lo activa: bp-advisor y bp-admin conservan el flujo de red.
      useDiscoveryDocumentCache: true,
    });

    this.oauthService.setStorage(localStorage);
    // this.oauthService.customTokenParameters = [];
    this.oauthService
      .loadDiscoveryDocumentAndTryLogin()
      .then(async () => {
        console.log('loadDiscoveryDocument true');
        this.authService.hasDiscoveredSubject$.next(true);
      })
      .catch(e => {
        // LogRocket.captureException(e);

        console.log('loadDiscoveryDocument false');
        this.authService.hasDiscoveredSubject$.next(false);
        localStorage.removeItem('bp-enter-as-id');
        localStorage.removeItem('bp_is_admin');
        Sentry.captureException(e);
        this.oauthService.logOut();

        // Swal.fire({
        //   iconHtml:
        //     '<i class="icon icon-swal fas fa-exclamation-triangle"></i>',
        //   customClass: {
        //     container: 'swal2-fixe',
        //     confirmButton: 'btn',
        //     cancelButton: 'btn'
        //   },
        //   buttonsStyling: false,
        //   showClass: {
        //     icon: 'swal2-noanimation'
        //   },
        //   hideClass: {
        //     icon: 'swal2-noanimation'
        //   },
        //   footer:
        //     '<div class="border"><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div><div class="border-section"></div></div>',
        //   icon: 'error',
        //   title: 'Error!',
        //   text:
        //     'Lo sentimos, no pudimos conectarnos con el servidor, si continua el problema contáctenos a traves del chat.'
        // });
      });
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.events
      .pipe(filter(e => e.type === 'token_received'))
      .subscribe((event: OAuthEvent) => {
        if (event instanceof OAuthSuccessEvent) {
          // Redirect to url by which the application was initially called.
          const state = this.oauthService.state;

          if (state) {
            const redirectUrl = decodeURIComponent(state);
            const redirectUrlOrigin = new URL(redirectUrl).origin;
            const redirectUrlPathname = new URL(redirectUrl).pathname;
            console.info('se redirige a', redirectUrlOrigin + '/auth/login#' + redirectUrlPathname);
            window.location.href = redirectUrlOrigin + '/auth/login#' + redirectUrlPathname;
          }
        }
      });
    this.oauthService.events.pipe(filter(e => e.type === 'token_received')).subscribe(async e => {
      const accessToken = this.oauthService.getAccessToken();
      this.wvCommunicationService.setAccessToken(accessToken);
      this.wvCommunicationService.isLogin(true);
    });
    this.oauthService.events
      .pipe(filter(e => e.type === 'session_terminated' || e.type === 'token_expires'))
      .subscribe(async e => {
        console.info('Your session has been terminated!');
        if (environment.name == 'Production') {
          Sentry.captureMessage('Your session has been terminated! ' + e.type);
        }
        localStorage.removeItem('bp-enter-as-id');
        localStorage.removeItem('bp_is_admin');
        this.oauthService.logOut();

        this.wvCommunicationService.isLogin(false);
        // const expiration = this.oauthService.getAccessTokenExpiration();
        // const storedAt = this.getAccessTokenStoredAt();
        // const timeout = this.calcTimeout(storedAt, expiration);
        // console.log('calc1', expiration, storedAt, timeout);

        // const expirationIdToken = this.oauthService.getIdTokenExpiration();
        // const storedAtIdToken = this.getIdTokenStoredAt();
        // const timeoutIdToken = this.calcTimeout(storedAt, expiration);

        // console.log(
        //   'calc IdToken ',
        //   expirationIdToken,
        //   storedAtIdToken,
        //   timeoutIdToken
        // );
        // console.log(
        //   'getIdTokenExpiration',
        //   this.oauthService.getIdTokenExpiration(),
        //   dayjs(this.oauthService.getIdTokenExpiration()).format('L LLL')
        // );
        // console.log(
        //   'getAccessTokenExpiration',
        //   this.oauthService.getAccessTokenExpiration(),
        //   dayjs(this.oauthService.getAccessTokenExpiration()).format('L LLL')
        // );
        // this.authService.hasDiscoveredSubject$
        //   .pipe(take(1))
        //   .subscribe(async result => {
        //     if (result) {
        //       try {
        //         console.log('refreshToken');
        //         const res = await this.oauthService.refreshToken();
        //         console.log(res);
        //       } catch (error) {
        //         console.error(error);
        //         this.oauthService.initCodeFlow();
        //       }
        //     }
        //   });

        // //   this.authService.logout(true);
      });

    this.oauthService.events.pipe(filter(e => e.type !== 'session_unchanged')).subscribe(e => {
      console.log(e);
    });
  }

  calcTimeout(storedAt: number, expiration: number): number {
    const timeoutFactor = this.oauthService?.timeoutFactor ?? 1;
    const now = Date.now();
    const delta = (expiration - storedAt) * timeoutFactor - (now - storedAt);
    return Math.max(0, delta);
  }
  getAccessTokenStoredAt(): number {
    return parseInt(localStorage.getItem('access_token_stored_at') ?? '1', 10);
  }
  getIdTokenStoredAt(): number {
    return parseInt(localStorage.getItem('id_token_stored_at') ?? '1', 10);
  }
  refreshForce(): void {
    this.oauthService.silentRefresh();
  }

  ngOnInit(): void {
    console.log('VERSION:', environment.version);
    console.log('MODO:', environment.name);

    // Mantiene legibles los iconos de la status bar del sistema cuando el portal corre
    // embebido en la app movil (donde se dibuja por debajo de ella). Va acá, en la raíz,
    // para que valga para los tres layouts sin que ninguno tenga que saberlo.
    this.bpNativeStatusBarService.start();

    //console.log(this.versionCheckService);

    //this.versionCheckService.initVersionCheck('/assets/version.json');

    (window as any).Intercom('boot', {
      app_id: environment.keyIntercom,
    });
    this.router.events.subscribe(event => {
      // if (event instanceof NavigationStart) {
      //   // Show loading indicator
      // }

      if (event instanceof NavigationEnd) {
        (window as any).Intercom('update');
        // Hide loading indicator
        // console.log(event);
      }

      // if (event instanceof NavigationError) {
      //   // Hide loading indicator

      //   // Present error to user
      //   console.log(event.error);
      // }
    });
  }

  ngOnDestroy(): void {
    // Limpiar conexión de Supabase
    this.maintenanceService.disconnect();
  }
}
